/**
 * Resolves RevenueCat appUserID when cookie-auth leaves `user.id` empty.
 * Uses numeric Django user id when available, else stable `artist_slug`.
 */

import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type { User } from '@/api/types';
import type { ArtistDetailsData } from '@/types/artistDetails';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { useAuthStore } from '@/stores/authStore';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import { getAuthCookies } from '@/utils/cookies';

const NUMERIC_ID_KEYS = [
  'artist_user_id',
  'user_id',
  'revenuecat_app_user_id',
  'app_user_id',
  'id',
  'artist_id',
] as const;

const SKIP_RECURSE_KEYS = new Set([
  'jobs',
  'subscriptions',
  'incompleted_sections',
  'profile_completion',
]);

export function isValidRevenueCatAppUserId(value: unknown): boolean {
  if (value == null) return false;
  const s = String(value).trim();
  if (!s) return false;
  const n = Number(s);
  return Number.isFinite(n) ? n > 0 : s.length > 0;
}

export function toRevenueCatAppUserIdString(value: unknown): string {
  return String(value).trim();
}

function isPositiveNumericId(value: unknown): boolean {
  if (value == null) return false;
  const n = Number(String(value).trim());
  return Number.isFinite(n) && n > 0;
}

function findNumericAppUserIdInResponse(node: unknown, depth = 0): string | null {
  if (depth > 10 || node == null || typeof node !== 'object') return null;
  const o = node as Record<string, unknown>;

  for (const key of NUMERIC_ID_KEYS) {
    const v = o[key];
    if (isPositiveNumericId(v)) {
      return toRevenueCatAppUserIdString(v);
    }
  }

  for (const [key, value] of Object.entries(o)) {
    if (SKIP_RECURSE_KEYS.has(key)) continue;
    if (value != null && typeof value === 'object') {
      const found = findNumericAppUserIdInResponse(value, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

function slugFromArtistDetailsData(data: ArtistDetailsData | null | undefined): string | null {
  const slug = data?.user_info?.artist_slug?.trim();
  if (isValidRevenueCatAppUserId(slug)) {
    return toRevenueCatAppUserIdString(slug);
  }
  return null;
}

function slugFromUnknownDetailsPayload(payload: unknown): string | null {
  if (payload == null || typeof payload !== 'object') return null;
  const root = payload as Record<string, unknown>;
  const data =
    root.data != null && typeof root.data === 'object'
      ? (root.data as ArtistDetailsData)
      : (payload as ArtistDetailsData);
  return slugFromArtistDetailsData(data);
}

function slugFromAuthCookies(): string | null {
  const { active_artist_slug } = getAuthCookies();
  if (isValidRevenueCatAppUserId(active_artist_slug)) {
    return toRevenueCatAppUserIdString(active_artist_slug);
  }
  return null;
}

function resolveFromUserRecord(user: User | null | undefined): string | null {
  if (isValidRevenueCatAppUserId(user?.id)) {
    return toRevenueCatAppUserIdString(user!.id);
  }
  return null;
}

function resolveSlugFallbacks(detailsPayload?: unknown): string | null {
  if (detailsPayload != null) {
    const fromPayload = slugFromUnknownDetailsPayload(detailsPayload);
    if (fromPayload) return fromPayload;
  }

  const cached = useArtistDetailsStore.getState().data;
  const fromCached = slugFromArtistDetailsData(cached);
  if (fromCached) return fromCached;

  return slugFromAuthCookies();
}

function resolveNumericFromDetails(detailsPayload?: unknown): string | null {
  if (detailsPayload != null) {
    const fromPayload = findNumericAppUserIdInResponse(detailsPayload);
    if (fromPayload) return fromPayload;
  }

  const cached = useArtistDetailsStore.getState().data;
  if (cached) {
    const fromCached = findNumericAppUserIdInResponse(cached);
    if (fromCached) return fromCached;
  }

  return null;
}

/** Persist resolved id so RC session + hooks stay in sync. */
export function persistRevenueCatAppUserId(resolvedId: string): void {
  const store = useAuthStore.getState();
  const current = store.user;
  if (!current || isValidRevenueCatAppUserId(current.id)) return;
  store.setUser({ ...current, id: resolvedId });
}

export function resolveRevenueCatAppUserIdSync(
  user?: User | null,
  detailsPayload?: unknown,
): string | null {
  const fromUser = resolveFromUserRecord(user ?? useAuthStore.getState().user);
  if (fromUser) return fromUser;

  const fromNumeric = resolveNumericFromDetails(detailsPayload);
  if (fromNumeric) return fromNumeric;

  const fromSlug = resolveSlugFallbacks(detailsPayload);
  if (fromSlug) return fromSlug;

  return null;
}

/**
 * Best-effort async resolution: auth store → persisted user → artist details → cookies.
 */
export async function resolveRevenueCatAppUserId(): Promise<string | null> {
  const authUser = useAuthStore.getState().user;

  const sync = resolveRevenueCatAppUserIdSync(authUser);
  if (sync) {
    persistRevenueCatAppUserId(sync);
    return sync;
  }

  const stored = await storageManager.get<User>(STORAGE_KEYS.USER_DATA);
  const fromStored = resolveFromUserRecord(stored);
  if (fromStored) {
    persistRevenueCatAppUserId(fromStored);
    return fromStored;
  }

  await useArtistDetailsStore.getState().ensureFetched();
  const afterEnsure = resolveRevenueCatAppUserIdSync(
    useAuthStore.getState().user,
    useArtistDetailsStore.getState().data,
  );
  if (afterEnsure) {
    persistRevenueCatAppUserId(afterEnsure);
    return afterEnsure;
  }

  try {
    const res = await apiClient.get(API_ENDPOINTS.AUTH.ARTIST_DETAILS);
    const fromApi = resolveRevenueCatAppUserIdSync(undefined, res.data);
    if (fromApi) {
      persistRevenueCatAppUserId(fromApi);
      return fromApi;
    }
  } catch {
    // 401/403 handled by global interceptors / artist details store
  }

  const fromCookie = slugFromAuthCookies();
  if (fromCookie) {
    persistRevenueCatAppUserId(fromCookie);
    return fromCookie;
  }

  return null;
}
